import { FC, useMemo, useState } from 'react';
import type { MaquinaOp } from '@/types/dashboards';
import { FILTROS_OPS, atendimento, contarFiltro, filtrarOps, situacaoDaOp, type FiltroOps } from '@/utils/equiplexMaquinas';
import { fmtDiaMes, fmtPct, fmtUn } from '@/utils/painelFormat';
import type { ModoTema } from '../chartTheme';
import PainelCard from '../PainelCard';
import { visualSituacaoOp } from './visual';

interface Props {
  ops: MaquinaOp[];
  maquinas: string[];
  itens: Record<string, string>;
  modo: ModoTema;
}

const LIMITE = 60;
const TH = 'px-2 py-2 text-left font-medium';

const Situacao: FC<{ op: MaquinaOp; modo: ModoTema }> = ({ op, modo }) => {
  const v = visualSituacaoOp(situacaoDaOp(op), modo);
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-content-primary">
      <span aria-hidden className="material-symbols-outlined text-[16px] leading-none" style={{ color: v.cor }}>
        {v.icone}
      </span>
      {v.rotulo}
    </span>
  );
};

const Linha: FC<{ op: MaquinaOp; descricao: string; modo: ModoTema }> = ({ op, descricao, modo }) => (
  <tr className="border-t border-theme-border align-top" data-testid={`maq-op-${op.op}`}>
    <td className="whitespace-nowrap px-2 py-2 tabular-nums">{fmtDiaMes(op.data)}</td>
    <td className="whitespace-nowrap px-2 py-2">{op.maquina}</td>
    <td className="px-2 py-2 text-xs">
      <span className="font-mono">{op.it_codigo}</span>
      {descricao && <span className="block text-content-muted">{descricao}</span>}
    </td>
    <td className="whitespace-nowrap px-2 py-2 font-mono text-xs">{op.lote || '—'}</td>
    <td className="whitespace-nowrap px-2 py-2 font-mono text-xs">{op.op}</td>
    <td className="px-2 py-2 text-right tabular-nums">{fmtUn(op.programado)}</td>
    <td className="px-2 py-2 text-right tabular-nums">{fmtUn(op.produzido)}</td>
    <td className="px-2 py-2 text-right tabular-nums">{fmtPct(atendimento(op))}</td>
    <td className="px-2 py-2 text-xs text-content-secondary">{op.estado_rotulo}</td>
    <td className="px-2 py-2">
      <Situacao op={op} modo={modo} />
    </td>
  </tr>
);

const Filtros: FC<{
  ops: MaquinaOp[];
  filtro: FiltroOps;
  onFiltro: (f: FiltroOps) => void;
  maquinas: string[];
  maquina: string;
  onMaquina: (m: string) => void;
}> = ({ ops, filtro, onFiltro, maquinas, maquina, onMaquina }) => (
  <div className="flex flex-wrap items-center gap-3">
    <div role="group" aria-label="Situação da OP" className="flex flex-wrap gap-1.5">
      {FILTROS_OPS.map((f) => (
        <button
          key={f.id}
          type="button"
          aria-pressed={filtro === f.id}
          onClick={() => onFiltro(f.id)}
          className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs ${
            filtro === f.id ? 'border-accent bg-accent font-semibold text-accent-contrast' : 'border-theme-border text-content-secondary hover:text-content-primary'
          }`}
        >
          {f.rotulo} ({contarFiltro(ops, f.id)})
        </button>
      ))}
    </div>
    <select
      aria-label="Filtrar por máquina"
      value={maquina}
      onChange={(e) => onMaquina(e.target.value)}
      className="rounded-lg border border-theme-border bg-surface-primary px-2 py-1 text-xs text-content-primary"
    >
      <option value="">Todas as máquinas</option>
      {maquinas.map((m) => (
        <option key={m} value={m}>
          {m}
        </option>
      ))}
    </select>
  </div>
);

/**
 * As OPs do mês com o que aconteceu com cada uma. "Sem apontamento" é o buraco que a planilha tapa
 * com o rendimento teórico; "encerrada sem produzir" é a OP que saiu do programado.
 */
const OpsMaquinaTable: FC<Props> = ({ ops, maquinas, itens, modo }) => {
  const [filtro, setFiltro] = useState<FiltroOps>('todas');
  const [maquina, setMaquina] = useState('');
  const [todas, setTodas] = useState(false);
  const linhas = useMemo(() => filtrarOps(ops, filtro, maquina), [ops, filtro, maquina]);
  const visiveis = todas ? linhas : linhas.slice(0, LIMITE);

  return (
    <PainelCard
      testId="maq-ops"
      titulo="OPs do mês"
      subtitulo="Pela data de início. Produzido = quantidade reportada na OP (como no CP0511), que chega no fim do processo. Só a encerrada sem produzir fica fora do programado."
    >
      <Filtros ops={ops} filtro={filtro} onFiltro={setFiltro} maquinas={maquinas} maquina={maquina} onMaquina={setMaquina} />
      {linhas.length === 0 ? (
        <p className="py-6 text-center text-sm text-content-muted">Nenhuma OP neste filtro.</p>
      ) : (
        <div className="relative overflow-x-auto">
          <table className="w-full text-sm text-content-primary">
            <thead className="text-xs uppercase tracking-wide text-content-muted">
              <tr>
                <th className={TH}>Início</th>
                <th className={TH}>Máquina</th>
                <th className={TH}>SKU</th>
                <th className={TH}>Lote</th>
                <th className={TH}>OP</th>
                <th className={`${TH} text-right`}>Programado</th>
                <th className={`${TH} text-right`}>Produzido</th>
                <th className={`${TH} text-right`}>%</th>
                <th className={TH}>No Datasul</th>
                <th className={TH}>Situação</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((op) => (
                <Linha key={op.op} op={op} descricao={itens[op.it_codigo] ?? ''} modo={modo} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {linhas.length > LIMITE && (
        <button type="button" onClick={() => setTodas((v) => !v)} className="self-start text-sm font-medium text-accent hover:underline">
          {todas ? 'Mostrar menos' : `Mostrar todas (${fmtUn(linhas.length)})`}
        </button>
      )}
    </PainelCard>
  );
};

export default OpsMaquinaTable;
